import { ReactElement, useMemo } from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	FormattedText,
	IconValue,
	SvgIconName,
	Typography
} from "client/Components";
import { isNotNilOrEmpty, setTrackingId } from "client/Misc/utils";
import { reactionIconMap } from "client/Features/Reactions/ReactionsCountButton/ReactionsCountButton";
import { InlineIcon } from "./InlineIcon/InlineIcon";
import { CommunityPostReactionNotificationDataViewModel } from "client/Api/Notifications/notifications";
import { preparePostTitleAndComment } from "./CommunityPostActivity";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { NumberFormatter } from "client/Hooks";
import { getComputedDisplayName } from "client/Features/AddressBook/common";

export type CommunityPostReactionActivityProps = {
	data: CommunityPostReactionNotificationDataViewModel;
};

export const CommunityPostReactionActivity = (props: CommunityPostReactionActivityProps): ReactElement => {
	const { data } = props;

	const {
		messagePreview,
		user: {
			firstName,
			lastName
		},
		reactionKind,
		otherReactionCount
	} = data;

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();
	const preparedPostTitleAndComment: string = preparePostTitleAndComment(messagePreview ?? "");

	const { computedDisplayName } = getComputedDisplayName({
		firstName,
		lastName
	});

	const iconValue: IconValue = reactionIconMap[reactionKind] !== undefined
		? {
			kind: "svg",
			name: reactionIconMap[reactionKind] as SvgIconName
		} : {
			kind: "font",
			name: "thumbs-up"
		};

	const titleKey = useMemo(
		(): LocalizationKey => {
			const isMultiple: boolean = otherReactionCount > 0;
			const hasPreviewText: boolean = isNotNilOrEmpty(preparedPostTitleAndComment);

			if (isMultiple) {
				return hasPreviewText
					? "_MultipleXReactedOnPostTitleY"
					: "_MultipleXReactedOnPost";
			}

			return hasPreviewText
				? "_XReactedOnPostTitleY"
				: "_XReactedOnPost";
		},
		[
			preparedPostTitleAndComment,
			otherReactionCount
		]
	);

	return (
		<span {...setTrackingId("title")}>
			<InlineIcon icon={iconValue}/>
			<FormattedText
				text={localize(titleKey)}
				thingsToReplace={[
					{
						what: "{{Name}}",
						filler: (
							<Typography
								weight="bold"
								trackingId="user-name"
							>
								{computedDisplayName}
							</Typography>
						)
					},
					{
						what: "{{Post}}",
						filler: (
							<Typography
								weight="bold"
								trackingId="comment"
							>
								{preparedPostTitleAndComment}
							</Typography>
						)
					},
					{
						what: "{{NumberOfUsers}}",
						filler: (
							<Typography
								weight="bold"
								trackingId="number-of-users"
							>
								+{statisticsFormatter(otherReactionCount)}
							</Typography>
						)
					}
				]}
			/>
		</span>
	);
};

CommunityPostReactionActivity.displayName = "CommunityPostReactionActivity";